import Lucide from "@/base-components/Lucide"
import { useDictionary } from "@/libs/DictionaryProvider"
import { Menu } from "@headlessui/react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Fragment, useRef, useState, useLayoutEffect, useEffect } from "react"
import { createPortal } from "react-dom"

export default function HoverMenu({ lang, menuItem }) {
  const buttonRef = useRef(null)
  const menuRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const { navigation } = useDictionary()
  const pathUrl = usePathname()

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setMenuPos({
        top: rect.bottom + window.scrollY - 120,
        left: rect.left + window.scrollX + 60,
      })
    }
  }

  // Position menu when open
  useLayoutEffect(() => {
    if (open) updatePosition()
  }, [open])

  // Keep position in sync on resize & scroll
  useEffect(() => {
    if (!open) return

    const observer = new ResizeObserver(updatePosition)
    if (buttonRef.current) observer.observe(buttonRef.current)

    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [open])

  const handleMouseEnter = () => setOpen(true)

  const handleMouseLeave = e => {
    const toElement = e.relatedTarget
    if (
      !toElement ||
      !(toElement instanceof Node) ||
      (!buttonRef.current.contains(toElement) &&
        !menuRef.current?.contains(toElement))
    ) {
      setOpen(false)
    }
  }

  return (
    <>
      <Menu as="div" className="w-full  inline-block">
        <Menu.Button as={Fragment}>
          <button
            ref={buttonRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-onboarding={
              menuItem.path.includes("tools/tracker")
                ? "tracker-nav"
                : undefined
            }
            className={`flex w-full text-xs hover:text-white font-semibold p-2 cursor-pointer rounded-lg hover:bg-white/5 ${
              pathUrl.includes(menuItem.path)
                ? "nav-gradient-closed text-white"
                : "text-white/80"
            }`}
          >
            <div className="w-full flex flex-col items-center text-white/80 hover:text-white">
              <span>
                <Lucide className="mr-0  " icon={menuItem.icon} />
              </span>
              <span className="mt-2  ">{menuItem.titleClosed}</span>
            </div>
          </button>
        </Menu.Button>
      </Menu>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id="menu-root"
            style={{
              position: "absolute",
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 9999,
              // background: "white",
              // border: "1px solid gray",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
            className="rounded-lg bg-dark border border-white/[0.12] font-semibold p-1 max-h-[200px] overflow-y-scroll no-scrollbar"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {menuItem.submenu.map((item, key) => (
              <div className="items-center" key={key}>
                <Link
                  href={`/${lang}/${item.path}` || "#"}
                  className="flex flex-col justify-center items-center text-xs text-white/70 hover:text-white py-2 px-4 rounded-md hover:bg-white/5"
                >
                  <Lucide className="mr-0" icon={item.icon} />
                  <span className="mt-1">{item.titleClosed}</span>
                </Link>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  )
}
